import { useEffect, useMemo, useState } from "react";
import { TrendingUp, Download, Search, X } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { statusLabel, statusColor } from "../lib/orderStatus";

const euro = (n) => `€${Number(n).toFixed(2).replace(".", ",")}`;

export const AdminRevenue = ({ onOpenOrder }) => {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [fType, setFType] = useState("all");
  const [fStatus, setFStatus] = useState("all");
  const [fDate, setFDate] = useState("");

  useEffect(() => {
    api.get("/admin/revenue").then(({ data }) => setRows(data)).catch(() => setRows([]));
  }, []);

  const filtered = useMemo(() => {
    if (!rows) return [];
    return rows.filter((r) => {
      if (q.trim()) {
        const s = q.toLowerCase();
        if (!((r.customer || "").toLowerCase().includes(s) || (r.email || "").toLowerCase().includes(s) || r.items.toLowerCase().includes(s))) return false;
      }
      if (fType !== "all" && r.plan !== fType) return false;
      if (fStatus !== "all" && r.status !== fStatus) return false;
      if (fDate && !String(r.created_at).startsWith(fDate)) return false;
      return true;
    });
  }, [rows, q, fType, fStatus, fDate]);

  const totalFiltered = filtered.filter((r) => r.status !== "annullato").reduce((s, r) => s + r.total, 0);

  const download = async (format) => {
    try {
      const { data } = await api.get("/admin/revenue/export", { params: { format }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `incassi-need-${new Date().toISOString().slice(0, 10)}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(format === "pdf" ? "PDF scaricato" : "File Excel scaricato");
    } catch { toast.error("Export non riuscito"); }
  };

  if (rows === null) return null;

  const inputCls = "rounded-full border border-ink/15 bg-panel px-4 py-2.5 text-sm text-ink outline-none focus:border-terra";

  return (
    <section className="mt-2" data-testid="admin-revenue">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <TrendingUp className="h-5 w-5 text-terra" strokeWidth={1.6} />
        <h2 className="font-serif text-2xl font-light text-ink">Incassi</h2>
        <span className="text-sm text-ink-soft">({filtered.length} righe · {euro(totalFiltered)} filtrati)</span>
        <div className="ml-auto flex gap-2">
          <button onClick={() => download("xlsx")} data-testid="export-excel" className="flex items-center gap-2 rounded-full bg-sage px-5 py-2.5 text-sm font-semibold text-cream hover:bg-sage-deep">
            <Download className="h-4 w-4" /> Excel
          </button>
          <button onClick={() => download("pdf")} data-testid="export-pdf" className="flex items-center gap-2 rounded-full border border-ink/15 px-5 py-2.5 text-sm font-semibold text-ink hover:bg-ink hover:text-cream">
            <Download className="h-4 w-4" /> PDF
          </button>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap gap-2" data-testid="revenue-filters">
        <div className="relative">
          <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-soft" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca cliente, email, prodotto…" data-testid="revenue-search" className={`${inputCls} w-64 pl-10`} />
          {q && <button onClick={() => setQ("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-soft hover:text-ink"><X className="h-3.5 w-3.5" /></button>}
        </div>
        <select value={fType} onChange={(e) => setFType(e.target.value)} data-testid="revenue-type-filter" className={inputCls}>
          <option value="all">Tutti i tipi</option>
          <option value="single">Acquisto singolo</option>
          <option value="box_mensile">Box Mensile</option>
        </select>
        <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} data-testid="revenue-status-filter" className={inputCls}>
          <option value="all">Tutti gli stati</option>
          {Object.entries(statusLabel).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <input type="date" value={fDate} onChange={(e) => setFDate(e.target.value)} data-testid="revenue-date-filter" className={inputCls} />
        {(q || fType !== "all" || fStatus !== "all" || fDate) && (
          <button onClick={() => { setQ(""); setFType("all"); setFStatus("all"); setFDate(""); }} data-testid="revenue-clear-filters" className="rounded-full border border-ink/15 px-4 py-2.5 text-xs text-ink-soft hover:text-ink">
            Azzera filtri
          </button>
        )}
      </div>

      <div className="overflow-x-auto rounded-2xl border border-ink/10">
        <table className="w-full min-w-[860px] text-sm" data-testid="revenue-table">
          <thead>
            <tr className="border-b border-ink/10 bg-panel text-left text-xs uppercase tracking-overline text-ink-soft">
              <th className="px-4 py-3">Data e ora</th>
              <th className="px-4 py-3">Cliente</th>
              <th className="px-4 py-3">Tipo</th>
              <th className="px-4 py-3">Prodotti</th>
              <th className="px-4 py-3">Pagamento</th>
              <th className="px-4 py-3">Stato</th>
              <th className="px-4 py-3 text-right">Totale</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.id} onClick={() => onOpenOrder?.(r.id)} data-testid={`revenue-row-${r.id}`} className="cursor-pointer border-b border-ink/5 transition-colors hover:bg-white/5">
                <td className="whitespace-nowrap px-4 py-3 text-ink">
                  {new Date(r.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" })}
                  <span className="ml-1.5 text-ink-soft">{new Date(r.created_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}</span>
                </td>
                <td className="px-4 py-3">
                  <span className="block text-ink">{r.customer}</span>
                  <span className="block text-xs text-ink-soft">{r.email}</span>
                </td>
                <td className="px-4 py-3">{r.plan === "box_mensile" ? <span className="rounded-full border border-sage/40 bg-sage/15 px-2.5 py-0.5 text-xs font-medium text-sage">Box</span> : <span className="text-ink-soft">Singolo</span>}</td>
                <td className="max-w-[220px] truncate px-4 py-3 text-ink-soft" title={r.items}>{r.items} <span className="text-xs">({r.items_count} pz)</span></td>
                <td className="whitespace-nowrap px-4 py-3 text-ink-soft">{r.payment}</td>
                <td className="px-4 py-3"><span className={`rounded-full border px-2.5 py-0.5 text-xs font-medium ${statusColor[r.status] || "bg-white/10 text-ink-soft border-white/20"}`}>{statusLabel[r.status] || r.status}</span></td>
                <td className="whitespace-nowrap px-4 py-3 text-right font-serif text-base text-ink">{euro(r.total)}</td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-ink-soft" data-testid="revenue-empty">Nessun incasso corrisponde ai filtri.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
};
